/*
 * portl-video-loader.js
 * ──────────────────────────────────────────────────────────────────
 * Global video loading indicator system. Attaches a spinner overlay
 * to any <video data-portl-video-loader> element on the page and
 * shows/hides it based on standard HTMLMediaElement loading events.
 *
 * USAGE — minimal (script auto-injects spinner DOM):
 *   <div class="my-wrapper">
 *     <video data-portl-video-loader src="..."></video>
 *   </div>
 *
 * USAGE — pre-rendered spinner (preferred for hero videos so the
 * spinner is visible before JS executes):
 *   <div class="my-wrapper">
 *     <video data-portl-video-loader src="..."></video>
 *     {% render 'portl-video-loader' %}
 *   </div>
 *
 * USAGE — programmatic (for sections that build videos dynamically,
 * e.g. carousel slides mounted via JS):
 *   window.PortlVideoLoader.attach(videoEl)
 *   window.PortlVideoLoader.detach(videoEl)
 *
 * SHOW EVENTS: loadstart, waiting, stalled
 * HIDE EVENTS: canplay, canplaythrough, playing, error
 *
 * Show on initial scan if video.readyState < 3 (HAVE_FUTURE_DATA).
 * Hide if already has enough buffered data to play through.
 *
 * Variants (set on the <video> element):
 *   data-portl-video-loader-dark   → dark spinner for light bgs
 *   data-portl-video-loader-sm     → small (36px)
 *   data-portl-video-loader-lg     → large (72px)
 *
 * ARCHITECTURE NOTES:
 *   • WeakSet tracks attached videos — re-attach is a no-op
 *   • No polling, no timers — pure event-driven
 *   • Auto-promotes parent to position:relative if it's static
 *   • Cleans up via element._portlVideoLoaderDetach() on detach
 *   • Respects Shopify theme editor — re-scans on
 *     shopify:section:load
 * ──────────────────────────────────────────────────────────────────
 */
(function () {
  'use strict';

  if (typeof window === 'undefined' || !window.document) return;

  /* Events that indicate the video is buffering / not yet ready
     to play. Show the spinner. */
  var SHOW_EVENTS = ['loadstart', 'waiting', 'stalled'];

  /* Events that indicate playback can proceed. Hide the spinner.
     `error` is included so a failed video doesn't leave the
     spinner running forever — the section's own error handler
     can show its own fallback state. */
  var HIDE_EVENTS = ['canplay', 'canplaythrough', 'playing', 'error'];

  /* WeakSet so attached <video> elements that get garbage-collected
     (removed from DOM) don't keep us alive. */
  var managed = new WeakSet();

  function variantClassFor(video) {
    var classes = ['portl-video-loader'];
    if (video.hasAttribute('data-portl-video-loader-dark')) classes.push('portl-video-loader--dark');
    if (video.hasAttribute('data-portl-video-loader-sm'))   classes.push('portl-video-loader--sm');
    if (video.hasAttribute('data-portl-video-loader-lg'))   classes.push('portl-video-loader--lg');
    return classes.join(' ');
  }

  function getOrCreateSpinner(video) {
    var parent = video.parentElement;
    if (!parent) return null;

    /* Look for a pre-rendered spinner sibling first (the
       {% render 'portl-video-loader' %} snippet). Match against
       data-portl-video-loader-injected so we don't accidentally
       grab a spinner that belongs to a different video. */
    var existing = parent.querySelector(':scope > .portl-video-loader[data-portl-video-loader-injected]');
    if (existing) return existing;

    /* Parent must establish a positioning context for the
       absolutely-positioned spinner to center over the video. */
    var pos = window.getComputedStyle(parent).position;
    if (pos === 'static') parent.style.position = 'relative';

    var spinner = document.createElement('div');
    spinner.className = variantClassFor(video);
    spinner.setAttribute('aria-hidden', 'true');
    spinner.setAttribute('data-portl-video-loader-injected', '');
    spinner.innerHTML =
      '<div class="portl-video-loader__ring"></div>' +
      '<span class="visually-hidden" aria-live="polite">Loading video…</span>';
    parent.appendChild(spinner);
    return spinner;
  }

  function attach(video) {
    if (!video || managed.has(video)) return;
    var spinner = getOrCreateSpinner(video);
    if (!spinner) return;
    managed.add(video);

    function show() { spinner.classList.add('is-visible'); }
    function hide() { spinner.classList.remove('is-visible'); }

    SHOW_EVENTS.forEach(function (e) { video.addEventListener(e, show); });
    HIDE_EVENTS.forEach(function (e) { video.addEventListener(e, hide); });

    /* Initial state.
         readyState >= 3 (HAVE_FUTURE_DATA) means the video can
         already start playing — no spinner needed.
         Anything less → show until first canplay/playing. */
    if (video.readyState >= 3) hide();
    else show();

    /* Stash a detach function on the element so sections that
       recreate DOM (e.g. carousel renderReels()) can clean up
       before removing the node. */
    video._portlVideoLoaderDetach = function () {
      SHOW_EVENTS.forEach(function (e) { video.removeEventListener(e, show); });
      HIDE_EVENTS.forEach(function (e) { video.removeEventListener(e, hide); });
      if (spinner.parentNode) spinner.parentNode.removeChild(spinner);
      managed.delete(video);
      delete video._portlVideoLoaderDetach;
    };
  }

  function detach(video) {
    if (video && typeof video._portlVideoLoaderDetach === 'function') {
      video._portlVideoLoaderDetach();
    }
  }

  function scan(root) {
    root = root || document;
    var nodes = root.querySelectorAll('video[data-portl-video-loader]');
    Array.prototype.forEach.call(nodes, attach);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { scan(); });
  } else {
    scan();
  }

  /* Shopify theme editor — re-scan when a section is reloaded
     (e.g. merchant changes a setting). */
  document.addEventListener('shopify:section:load', function (e) {
    if (e && e.target) scan(e.target);
  });

  /* Public API — for sections that:
       • Build videos dynamically (carousels, modals)
       • Need to attach/detach manually
       • Want to force a re-scan after their own DOM mutation */
  window.PortlVideoLoader = {
    attach: attach,
    detach: detach,
    scan: scan
  };
})();
